import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useRenewalForecast } from '@/services/dashboardService';
import { formatCurrency } from '@/utils/formatters';
import { RenewalForecastGrouping } from '@/types';
import { GroupByToggle } from './GroupByToggle';
import { RenewalForecastChart } from './RenewalForecastChart';
import { RenewalForecastTable } from './RenewalForecastTable';
import { rowsOfPeriod } from './forecastRows';

const MONTHS = 12;
const NO_PERIOD = 'none';

/**
 * Dashboard card for the renewal forecast (#18): a stacked bar per period (auto-renews versus
 * decision needed) over the next 12 months, a Month / Quarter toggle that re-groups in place, and
 * a drill-down table for the selected period. A period is selected by clicking its bar or from the
 * period select, which also reaches periods whose contracts are worth nothing (no bar to click) and
 * works from the keyboard. Lives in its own component so Dashboard.tsx only gains one element.
 */
export const RenewalForecastCard: React.FC = () => {
  const [groupBy, setGroupBy] = React.useState<RenewalForecastGrouping>(RenewalForecastGrouping.Month);
  const [selection, setSelection] = React.useState<{ groupBy: RenewalForecastGrouping; key: string | null }>({
    groupBy: RenewalForecastGrouping.Month,
    key: null,
  });
  // Re-grouping clears the selection for good, so Month, Quarter, Month does not restore it
  if (selection.groupBy !== groupBy) {
    setSelection({ groupBy, key: null });
  }
  const selectedKey = selection.groupBy === groupBy ? selection.key : null;
  const select = (key: string | null) => setSelection({ groupBy, key });

  const { data: forecast, isLoading, isPlaceholderData, error } = useRenewalForecast(MONTHS, groupBy);
  // While the other grouping loads, the previous one is still drawn: dim it and keep it inert
  const isStale = isPlaceholderData;
  const selectedPeriod = selectedKey && !isStale ? forecast?.periods.find((p) => p.key === selectedKey) ?? null : null;
  const hasContracts = (forecast?.contractCount ?? 0) > 0;
  const periodsWithContracts = forecast?.periods.filter((p) => p.contractCount > 0) ?? [];

  const scope =
    groupBy === RenewalForecastGrouping.Quarter
      ? 'in the quarters covering the next 12 months'
      : 'in the next 12 months';

  return (
    <Card className="mb-8" data-testid="renewal-forecast-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Renewal Forecast</CardTitle>
            <CardDescription>
              Contracts renewing or expiring {scope}
              {forecast && !isStale ? `: ${forecast.contractCount} contracts, ${formatCurrency(forecast.totalAmount)}` : ''}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <GroupByToggle value={groupBy} onChange={setGroupBy} />
            <Link
              to="/renewals"
              search={{ groupBy, months: MONTHS }}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline whitespace-nowrap"
            >
              Open full forecast
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[300px] w-full" />
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>Failed to load the renewal forecast: {error.message}</AlertDescription>
          </Alert>
        ) : forecast && hasContracts ? (
          <>
            <div className={cn(isStale && 'opacity-60 pointer-events-none')} aria-busy={isStale}>
              <RenewalForecastChart periods={forecast.periods} selectedKey={selectedKey} onSelect={select} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-grey-500">Click a bar or pick a period:</span>
              <Select
                value={selectedKey ?? NO_PERIOD}
                onValueChange={(value) => select(value === NO_PERIOD ? null : value)}
                disabled={isStale}
              >
                <SelectTrigger className="w-[220px] h-8 text-sm" aria-label="Show contracts for period" data-testid="renewal-forecast-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PERIOD}>No period selected</SelectItem>
                  {periodsWithContracts.map((period) => (
                    <SelectItem key={period.key} value={period.key}>
                      {period.label} ({period.contractCount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedPeriod && (
              <div className="mt-4" data-testid="renewal-forecast-drilldown">
                <h3 className="text-sm font-semibold text-grey-800 mb-2">
                  {selectedPeriod.label}: {selectedPeriod.contractCount}{' '}
                  {selectedPeriod.contractCount === 1 ? 'contract' : 'contracts'}, {formatCurrency(selectedPeriod.totalAmount)}
                </h3>
                <RenewalForecastTable rows={rowsOfPeriod(selectedPeriod)} testId="renewal-forecast-drilldown-table" />
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-[300px]">
            <p className="text-sm text-grey-500">No renewals {scope}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
